import { useEffect } from "react";
import { InternalAxiosRequestConfig } from "axios";
import { StatusCodes } from "http-status-codes";
import api from "@/api/api";
import Logger from "@/utils/Logger";
import { AuthService } from "@/service/AuthService";

interface UseAxiosInterceptorProps {
  accessToken: string | null;
  onUnauthorized: () => void;
}

/**
 * Custom hook to set up Axios interceptors for authentication.
 * Attaches Bearer token to external requests and handles 401 responses.
 */
export const useAxiosInterceptor = ({
  accessToken,
  onUnauthorized,
}: UseAxiosInterceptorProps) => {
  useEffect(() => {
    /**
     * Request interceptor: Attaches Bearer token to external requests
     */
    const requestInterceptor = (request: InternalAxiosRequestConfig) => {
      const isExternalRequest = request.baseURL !== window.location.origin;

      if (isExternalRequest && accessToken) {
        request.headers.Authorization = `Bearer ${accessToken}`;
        Logger.debug("Bearer token attached to request", ["AuthAxiosInterceptor"]);
      } else if (isExternalRequest && !accessToken) {
        Logger.warn("No access token available for external request", [
          "AuthAxiosInterceptor",
        ]);
      } else {
        Logger.debug("Sending unauthenticated request", ["AuthAxiosInterceptor"]);
      }

      return request;
    };

    /**
     * Response error interceptor: Handles 401 Unauthorized responses
     */
    const responseErrorInterceptor = (error: unknown) => {
      const failedAxiosRequestConfig = (error as any)?.config as
        | (InternalAxiosRequestConfig & { _retry?: boolean })
        | null;

      const isUnauthorized = (error as any)?.response?.status === StatusCodes.UNAUTHORIZED;
      const hasConfig = !!failedAxiosRequestConfig;
      const notRetried = !failedAxiosRequestConfig?._retry;

      // Handle 401 Unauthorized with ensured single retry to prevent loops
      if (isUnauthorized && hasConfig && notRetried) {
        failedAxiosRequestConfig!._retry = true;

        Logger.error(
          "Received 401 Unauthorized - token expired or invalid",
          error,
          ["AuthAxiosInterceptor"],
        );

        // Save current path before triggering re-authentication
        // (will be no-op if already saved)
        AuthService.savePostLoginRedirect();

        // Trigger re-authentication flow
        try {
          Logger.info("Invoking onUnauthorized handler", ["AuthAxiosInterceptor"]);
          onUnauthorized();
        } catch (e) {
          Logger.error("onUnauthorized handler threw an error", e, [
            "AuthAxiosInterceptor",
          ]);
        }
      }

      return Promise.reject(error);
    };

    // Register interceptors
    const requestInterceptorId = api.interceptors.request.use(
      requestInterceptor,
      (error) => Promise.reject(error),
    );

    const responseInterceptorId = api.interceptors.response.use(
      (response) => response,
      responseErrorInterceptor,
    );

    Logger.info("Axios interceptors registered", ["AuthAxiosInterceptor"]);

    // Cleanup: Eject interceptors on unmount
    return () => {
      api.interceptors.request.eject(requestInterceptorId);
      api.interceptors.response.eject(responseInterceptorId);
      Logger.info("Axios interceptors ejected", ["AuthAxiosInterceptor"]);
    };
  }, [accessToken, onUnauthorized]);
};